import { BUILD } from '../../config';
import {
  footprint, footprintTerrain, polyOverlap, FootprintShape,
} from './grid';
import type {
  PlacedPiece, ResolvedSlot, SlotKind,
} from './types';

// The validator runs the placement gates in a fixed order and returns the FIRST failure with an
// explicit reason code (so the HUD/debug can say exactly why a ghost is red): occupancy -> Box3
// overlap -> piece rules -> support. The foundation terrain-fit and ceiling overlap rules are the
// §B / updateCeiling math copied verbatim, run as RULES (not anchor distance tests).

// --- occupancy: a placed-slot ledger keyed by resolved world key + kind ---

export class OccupancyLedger {
  // worldKey -> set of slot kinds currently occupying it.
  private readonly slots = new Map<string, Set<SlotKind>>();

  reserve(resolved: ResolvedSlot[]): void {
    for (const s of resolved) {
      let set = this.slots.get(s.worldKey);
      if (!set) { set = new Set(); this.slots.set(s.worldKey, set); }
      set.add(s.kind);
    }
  }

  free(resolved: ResolvedSlot[]): void {
    for (const s of resolved) {
      const set = this.slots.get(s.worldKey);
      if (!set) continue;
      set.delete(s.kind);
      if (set.size === 0) this.slots.delete(s.worldKey);
    }
  }

  // True if any of `resolved` would collide with an existing occupant it is exclusiveWith.
  conflicts(resolved: ResolvedSlot[]): boolean {
    for (const s of resolved) {
      const set = this.slots.get(s.worldKey);
      if (!set) continue;
      for (const kind of set) {
        if (s.exclusiveWith.includes(kind)) return true;
      }
    }
    return false;
  }
}

// --- rule bodies (verbatim math from building.ts) ---

// §B terrain-grounded foundation fit: legs reach the ground AND rising ground does not bury the
// floor by more than a half-wall. `floor` is the chosen walkable-top Y; `type` the footprint shape.
export function foundationTerrainFit(type: FootprintShape, x: number, z: number, yaw: number, floor: number): boolean {
  const HALF = BUILD.wallHeight / 2;
  const DEPTH = BUILD.foundDepth;
  const { minC, maxC } = footprintTerrain(type, x, z, yaw);
  const legsReach = minC >= floor - DEPTH - 1e-3; // every corner leg can touch the ground
  const notBuried = maxC <= floor + HALF + 1e-3;  // rising ground does not swallow the floor
  return legsReach && notBuried;
}

// SAT overlap of a footprint against existing footprints (ignoring height — adjacent tiers step).
export function footprintNoOverlap(type: FootprintShape, x: number, z: number, yaw: number, placed: PlacedPiece[]): boolean {
  const a = footprint(type, x, z, yaw);
  for (const p of placed) {
    if (p.family === 'none') continue;
    if (polyOverlap(a, footprint(p.family as FootprintShape, p.position.x, p.position.z, p.yaw))) return false;
  }
  return true;
}

// Ceiling overlap: a ceiling footprint here must not overlap an existing footprint at ~the same
// height (±0.3 band). Different storeys are fine.
export function ceilingNoOverlap(type: FootprintShape, x: number, z: number, yaw: number, top: number, placed: PlacedPiece[]): boolean {
  const a = footprint(type, x, z, yaw);
  for (const p of placed) {
    if (p.family === 'none' || p.top === undefined) continue;
    if (Math.abs(p.top - top) > 0.3) continue;
    if (polyOverlap(a, footprint(p.family as FootprintShape, p.position.x, p.position.z, p.yaw))) return false;
  }
  return true;
}

